import React, { useState, useCallback, useEffect, useId } from 'react';
import { isValidCron } from 'cron-validator';
import cronstrue from 'cronstrue';

/**
 * Cron expression input with real-time validation and human-readable explanation
 * @param {string} value - Cron expression string
 * @param {Function} onChange - Value change callback
 * @param {boolean} disabled - Whether the input is disabled
 * @param {string} className - Additional CSS classes
 */
export const CronInput = React.memo(
    ({ value = '', onChange, disabled = false, className = '' }) => {
        const [isValid, setIsValid] = useState(true);
        const [explanation, setExplanation] = useState('');
        const [validationError, setValidationError] = useState('');
        const inputId = useId();
        const errorId = `${inputId}-error`;

        // Validate and explain cron expression
        const validateCron = useCallback(cronExpression => {
            if (!cronExpression.trim()) {
                setIsValid(true);
                setExplanation('');
                setValidationError('');
                return;
            }

            try {
                const valid = isValidCron(cronExpression, { seconds: false });

                if (valid) {
                    const humanReadable = cronstrue.toString(cronExpression, {
                        throwExceptionOnParseError: false,
                        verbose: false,
                        use24HourTimeFormat: true,
                    });

                    setIsValid(true);
                    setExplanation(humanReadable);
                    setValidationError('');
                } else {
                    setIsValid(false);
                    setExplanation('');
                    setValidationError('Invalid cron expression format');
                }
            } catch (error) {
                setIsValid(false);
                setExplanation('');
                setValidationError(error.message || 'Invalid cron expression');
            }
        }, []);

        useEffect(() => {
            validateCron(value);
        }, [value, validateCron]);

        const handleChange = useCallback(
            e => {
                const newValue = e.target.value;
                onChange(newValue);
            },
            [onChange]
        );

        return (
            <div className={`mb-4 ${className}`}>
                <label htmlFor={inputId} className="block text-sm font-medium text-fg-muted mb-2">
                    Cron Expression
                </label>

                <input
                    id={inputId}
                    type="text"
                    value={value}
                    onChange={handleChange}
                    disabled={disabled}
                    aria-invalid={!isValid}
                    aria-describedby={validationError ? errorId : undefined}
                    placeholder="0 9 * * 1-5  (9 AM on weekdays)"
                    className={`
                    w-full px-3 py-2 border rounded-lg min-h-11
                    bg-input text-fg font-mono text-sm
                    transition-colors duration-200
                    ${
                        disabled
                            ? 'opacity-50 cursor-not-allowed bg-surface-disabled'
                            : 'hover:border-border-hover focus:outline-none focus:ring-2'
                    }
                    ${
                        !isValid && value.trim()
                            ? 'border-error focus:ring-error focus:border-error'
                            : 'border-border focus:ring-primary focus:border-primary'
                    }
                `}
                />

                {/* Help text */}
                <div className="flex flex-wrap items-center gap-2 text-xs text-fg-subtle mt-1">
                    <span>Format: minute hour day month weekday</span>
                    <a
                        href="https://crontab.guru"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center min-h-11 text-fg hover:text-primary-hover underline"
                    >
                        crontab.guru helper
                    </a>
                </div>

                {/* Real-time explanation or error */}
                {value.trim() && (
                    <div className="mt-2">
                        {isValid && explanation ? (
                            <div className="p-3 bg-surface-alt border border-border-light rounded-lg">
                                <div className="text-sm text-success">
                                    <strong>Schedule:</strong> {explanation}
                                </div>
                            </div>
                        ) : validationError ? (
                            <div className="p-3 bg-surface-alt border border-border-light rounded-lg">
                                <div id={errorId} className="text-sm text-error">
                                    <strong>Error:</strong> {validationError}
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}

                {/* Common examples */}
                {!value.trim() && (
                    <div className="mt-2 text-xs text-fg-subtle">
                        <div className="font-medium mb-1">Common examples:</div>
                        <div className="space-y-1">
                            <div>
                                <code className="bg-surface-alt px-1 py-0.5 rounded">
                                    0 9 * * *
                                </code>{' '}
                                - Daily at 9:00 AM
                            </div>
                            <div>
                                <code className="bg-surface-alt px-1 py-0.5 rounded">
                                    0 9 * * 1-5
                                </code>{' '}
                                - Weekdays at 9:00 AM
                            </div>
                            <div>
                                <code className="bg-surface-alt px-1 py-0.5 rounded">
                                    0 0 1 * *
                                </code>{' '}
                                - First day of every month
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

CronInput.displayName = 'CronInput';

export default CronInput;
